import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead } from 'api/notificationApi';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const fetchCount = async () => {
    try {
      const countData = await getUnreadCount();
      setUnreadCount(countData?.unread_count ?? 0);
    } catch (error) {
      console.error('Failed to fetch unread count', error);
    }
  };

  const fetchNotificationData = async () => {
    try {
      setLoading(true);
      const [list, countData] = await Promise.all([
        getNotifications({ per_page: 15 }),
        getUnreadCount()
      ]);
      setNotifications(Array.isArray(list) ? list : list?.data || []);
      setUnreadCount(countData?.unread_count ?? 0);
    } catch (error) {
      console.error('Failed to fetch notifications or unread count', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch of unread count on mount
    fetchCount();

    // Background interval every 30 seconds:
    // Only poll the lightweight count endpoint when popover is closed to conserve bandwidth & DB load.
    const interval = setInterval(() => {
      if (open) {
        fetchNotificationData();
      } else {
        fetchCount();
      }
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [open]);

  const handleToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) {
      fetchNotificationData();
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMarkAsRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read', error);
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        id="btn-header-notification-bell"
        color="secondary"
        variant="light"
        sx={{
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent'
        }}
        aria-label="notifications"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge
          id="badge-unread-notifications"
          badgeContent={unreadCount}
          color="error"
          max={99}
          showZero={false}
        >
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 320, maxWidth: { xs: 320, md: 440 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title={
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6">Notifications</Typography>
                        {unreadCount > 0 && (
                          <Chip
                            label={`${unreadCount} Unread`}
                            color="error"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Stack>
                      {unreadCount > 0 && (
                        <Button
                          id="btn-mark-all-read-dropdown"
                          size="small"
                          onClick={handleMarkAllRead}
                          startIcon={<CheckOutlined />}
                          sx={{ textTransform: 'none', fontSize: '0.75rem', p: 0.5 }}
                        >
                          Mark all read
                        </Button>
                      )}
                    </Stack>
                  }
                  elevation={0}
                  border={false}
                  content={false}
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      maxHeight: '400px',
                      overflow: 'auto',
                      '& .MuiListItemButton-root': {
                        py: 1,
                        px: 2,
                        '&.Mui-selected': { bgcolor: '#eff6ff', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {loading && notifications.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : notifications.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          No notifications found.
                        </Typography>
                      </Box>
                    ) : (
                      notifications.map((notification) => (
                        <ListItem
                          key={notification.id}
                          component={ListItemButton}
                          divider
                          selected={!notification.read_at}
                          onClick={() => !notification.read_at && handleMarkAsRead(notification.id)}
                          sx={{
                            backgroundColor: notification.read_at ? 'transparent' : '#f0fdf4'
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                color: notification.type === 'warning' ? 'warning.main' : 'primary.main',
                                bgcolor: notification.type === 'warning' ? 'warning.lighter' : 'primary.lighter'
                              }}
                            >
                              {notification.type === 'warning' ? <WarningOutlined /> : <InfoCircleOutlined />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={notification.read_at ? 400 : 700}>
                                  {notification.title}
                                </Typography>
                                {!notification.read_at && (
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e', ml: 1 }} />
                                )}
                              </Stack>
                            }
                            secondary={
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.3 }}>
                                  {notification.message}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '10px' }}>
                                    {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </Typography>
                                  {notification.email_status && (
                                    <Chip
                                      label={`Email: ${notification.email_status}`}
                                      size="small"
                                      color={notification.email_status === 'sent' ? 'success' : notification.email_status === 'failed' ? 'error' : 'default'}
                                      variant="outlined"
                                      sx={{ height: 16, fontSize: '9px', px: 0.5 }}
                                    />
                                  )}
                                </Stack>
                              </Stack>
                            }
                          />
                        </ListItem>
                      ))
                    )}

                    <Divider />
                    <ListItemButton
                      id="btn-view-all-notifications"
                      sx={{ textAlign: 'center', py: `${12}px !important` }}
                      onClick={handleViewAll}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" color="primary" fontWeight={600}>
                            View All Notifications &amp; Email Delivery History &rarr;
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
